import { create } from 'zustand';

const STORAGE_KEY = 'scraperProgressStore';

const loadFromStorage = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

function persist(progress) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
}

export const useScraperProgressStore = create((set) => ({
  progress: loadFromStorage(),

  startScraper: (code, options = {}) => {
    set((state) => {
      const next = {
        ...state.progress,
        [code]: {
          isRunning: true,
          startTime: Date.now(),
          endTime: null,
          status: 'running',
          message: 'Starting scraper...',
          terminalMessage: null,
          electionCount: 0,
          pagesVisited: 0,
          errors: [],
          lastError: null,
          cacheMode: options.cacheMode || 'off',
          verbose: options.verbose || false,
          artifactMetadata: null,
          scheduleWindowsFound: null,
        },
      };
      persist(next);
      return { progress: next };
    });
  },

  updateProgress: (code, update) => {
    set((state) => {
      const next = {
        ...state.progress,
        [code]: {
          ...state.progress[code],
          ...update,
        },
      };
      persist(next);
      return { progress: next };
    });
  },

  addError: (code, error) => {
    const errorObj =
      typeof error === 'string'
        ? { message: error, timestamp: Date.now() }
        : {
            message: String(error?.message || error),
            code: error?.code,
            url: error?.url,
            timestamp: Date.now(),
          };

    set((state) => {
      const prev = state.progress[code] || {};
      const next = {
        ...state.progress,
        [code]: {
          ...prev,
          status: 'error',
          lastError: errorObj,
          terminalMessage: errorObj.message,
          errors: [...(prev.errors || []), errorObj],
        },
      };
      persist(next);
      return { progress: next };
    });
  },

  completeScraper: (code, result = {}) => {
    set((state) => {
      const prev = state.progress[code] || {};
      const hasErrors = (prev.errors || []).length > 0 || result.status === 'error';

      const nextEntry = {
        ...prev,
        isRunning: false,
        endTime: Date.now(),
        status: hasErrors ? 'error' : 'completed',
        message: hasErrors ? prev.message || 'Scraper finished with errors' : 'Scraper completed',
        terminalMessage: hasErrors
          ? prev.lastError?.message || result.message || 'Scraper finished with errors'
          : result.message || 'Scraper completed',
        debugMessage: result.debugMessage || null,
      };

      const next = {
        ...state.progress,
        [code]: nextEntry,
      };

      persist(next);
      return { progress: next };
    });
  },

  setArtifactMetadata: (code, metadata) => {
    set((state) => {
      const next = {
        ...state.progress,
        [code]: {
          ...state.progress[code],
          artifactMetadata: metadata,
        },
      };
      persist(next);
      return { progress: next };
    });
  },

  clearProgress: (code) => {
    set((state) => {
      const next = { ...state.progress };
      delete next[code];
      persist(next);
      return { progress: next };
    });
  },
}));
